
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";

const TermsAndConditions = () => (
  <Layout>
    <Helmet>
      <title>Terms & Conditions - TrueCore Gadgets | Legal Terms & Service Agreement</title>
      <meta name="description" content="Read TrueCore Gadgets terms and conditions. Legal terms for using our website, purchasing products, and accessing our services. Last updated June 2023." />
      <meta name="keywords" content="TrueCore terms conditions, legal terms, service agreement, website terms, purchase terms, user agreement" />
      
      {/* Canonical URL - Fixed to use consistent www subdomain */}
      <link rel="canonical" href="https://www.truecoregadgets.com/terms-and-conditions" />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Terms & Conditions - TrueCore Gadgets" />
      <meta property="og:description" content="Legal terms and conditions for using TrueCore Gadgets website and services." />
      <meta property="og:url" content="https://www.truecoregadgets.com/terms-and-conditions" />
      <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Terms & Conditions - TrueCore Gadgets" />
      <meta name="twitter:description" content="Legal terms and conditions for using TrueCore Gadgets services." />
      <meta name="twitter:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      
      {/* Additional meta tags for better indexing */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
    </Helmet>
    
    <div className="container mx-auto py-12 px-4 max-w-4xl">
      <h1 className="text-4xl font-bold mb-8 text-primary text-center">Terms & Conditions</h1>
      
      <div className="bg-muted/50 rounded-lg p-6 mb-8">
        <p className="text-lg">
          Welcome to TrueCore Gadgets (operated by Truecore Innovations). These Terms & Conditions ("Terms") govern your use of our website, products, and services. By accessing or using our services, you agree to be bound by these Terms.
        </p>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">1. Acceptance of Terms</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              By accessing, browsing, or using the TrueCore Gadgets website (truecoregadgets.com) or purchasing any products from us, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p>
              If you do not agree with any part of these Terms, you must not use our website or services. We reserve the right to modify these Terms at any time, and such modifications will be effective immediately upon posting.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">2. Company Information</h2>
          <div className="bg-card p-6 rounded-lg border">
            <p className="mb-2"><strong>Company Name:</strong> Truecore Innovations</p>
            <p className="mb-2"><strong>Business Address:</strong> Mumbai, Maharashtra, India</p>
            <p className="mb-2"><strong>Email:</strong> support@truecoregadgets.com</p>
            <p className="mb-2"><strong>Phone:</strong> 022 6962 1901</p>
            <p><strong>Website:</strong> https://truecoregadgets.com</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">3. Products and Services</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              TrueCore Gadgets specializes in manufacturing and selling high-quality electronic devices including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Android tablets and custom tablet solutions</li>
              <li>Personal computers and workstations</li>
              <li>Smart gadgets and IoT devices</li>
              <li>Custom hardware solutions for businesses</li>
              <li>Bulk orders and enterprise solutions</li>
            </ul>
            <p>
              All product descriptions, specifications, and pricing are subject to change without notice. We strive to ensure accuracy but cannot guarantee that all information is error-free.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">4. User Accounts and Registration</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              When creating an account or placing an order, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">5. Orders and Payment</h2>
          <div className="space-y-4 text-muted-foreground">
            <h3 className="text-lg font-semibold text-foreground">Order Processing</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>All orders are subject to acceptance and availability</li>
              <li>We reserve the right to cancel orders for any reason</li>
              <li>Order confirmation does not guarantee product availability</li>
              <li>Bulk orders may require additional verification</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-foreground">Payment Terms</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Payment is required before product shipment</li>
              <li>We accept major credit cards, bank transfers, and approved payment methods</li>
              <li>All prices are in Indian Rupees (INR) unless otherwise stated</li>
              <li>Prices include applicable taxes as per Indian tax laws</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">6. Shipping and Delivery</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Detailed shipping terms are outlined in our <a href="/shipping-policy" className="text-primary underline">Shipping Policy</a>. Key points include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Delivery times are estimates and not guaranteed</li>
              <li>Risk of loss passes to buyer upon shipment</li>
              <li>International shipping may incur additional customs duties</li>
              <li>Damaged shipments must be reported within 48 hours</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">7. Returns and Refunds</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Our return and refund policy is detailed in our <a href="/refund-policy" className="text-primary underline">Refund Policy</a>. Summary:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>7-day return window for eligible products</li>
              <li>Products must be in original, unused condition</li>
              <li>Custom orders and personalized products are non-returnable</li>
              <li>Return shipping costs may apply</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">8. Intellectual Property Rights</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              All content on this website, including but not limited to text, graphics, logos, images, software, and design elements, are the property of Truecore Innovations or its licensors and are protected by:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Indian and international copyright laws</li>
              <li>Trademark laws and regulations</li>
              <li>Trade secret protections</li>
              <li>Other intellectual property rights</li>
            </ul>
            <p>
              You may not reproduce, distribute, modify, or create derivative works without our express written permission.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">9. Prohibited Uses</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>You agree not to use our website or services to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Transmit harmful, offensive, or illegal content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the proper functioning of the website</li>
              <li>Collect user information without consent</li>
              <li>Engage in any fraudulent or deceptive practices</li>
              <li>Use our products for illegal or harmful purposes</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">10. Warranties and Disclaimers</h2>
          <div className="space-y-4 text-muted-foreground">
            <h3 className="text-lg font-semibold text-foreground">Product Warranties</h3>
            <p>
              Our products come with manufacturer warranties as detailed in our <a href="/warranty-policy" className="text-primary underline">Warranty Policy</a>. These warranties are in addition to your statutory rights under Indian consumer protection laws.
            </p>
            
            <h3 className="text-lg font-semibold text-foreground">Website Disclaimer</h3>
            <p>
              The information on this website is provided "as is" without warranties of any kind. While we strive for accuracy, we do not warrant that the website will be error-free, uninterrupted, or free from harmful components.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">11. Limitation of Liability</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              To the maximum extent permitted by law, Truecore Innovations shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Loss of profits or revenue</li>
              <li>Data loss or corruption</li>
              <li>Business interruption</li>
              <li>Loss of goodwill or reputation</li>
            </ul>
            <p>
              Our total liability for any claim shall not exceed the amount paid by you for the specific product or service giving rise to the claim.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">12. Governing Law and Jurisdiction</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from or relating to these Terms or your use of our services shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">13. Force Majeure</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including but not limited to natural disasters, war, terrorism, labor disputes, government actions, or supply chain disruptions.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">14. Contact Information</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              If you have any questions about these Terms & Conditions, please contact us:
            </p>
            <div className="bg-card p-6 rounded-lg border">
              <p className="mb-2"><strong>Email:</strong> legal@truecoregadgets.com</p>
              <p className="mb-2"><strong>Phone:</strong> 022 6962 1901</p>
              <p><strong>Address:</strong> Truecore Innovations, Mumbai, Maharashtra, India</p>
            </div>
          </div>
        </section>
      </div>

      <div className="mt-12 p-6 bg-primary/10 rounded-lg text-center">
        <p className="text-sm text-muted-foreground">
          <strong>Last updated:</strong> June 2023<br/>
          These Terms & Conditions are effective as of the last updated date and supersede all prior versions.
        </p>
      </div>
    </div>
  </Layout>
);

export default TermsAndConditions;
